import { BRAND_NAMES, IMAGEN_SUPPORTED_BRANDS, AIDA_SUBSCRIPTION_TYPES } from "@/constants/constants";
import { BrandWithCdsMetadata } from "@/service/entity/brand/types";
import { getArrayFromCSVString } from "./string.utils";

export const normalizeBrandId = (id: string) => {
  if (!id) return "";

  if (["oreo - brazil", "oreo-brazil"].includes(id.toLowerCase())) {
    return "oreo_br";
  }

  if (["oreo be-mx", "oreo be - mx"].includes(id.toLowerCase())) {
    return "oreo_be_mx";
  }

  if (["oreo be-de", "oreo be - de"].includes(id.toLowerCase())) {
    return "oreo_be_de";
  }

  if (["ritz-de"].includes(id.toLowerCase())) {
    return "ritz_de";
  }

  const normalizedId = id
    ?.toLowerCase()
    ?.replaceAll(/[^a-z\s-]/g, "")
    ?.split("-")[0]
    ?.trim();

  switch (normalizedId) {
    case "chipsahoy":
    case "chips ahoy":
    case "chips ahoy!":
      return "chips";

    case "chips ahoy dcomm":
      return "chips_us_dcomm";

    case "oreo":
      return "oreo";

    case "milka":
      return "milka";

    case "cadbury":
      return "cadbury_uk";

    case "lacta":
    case "lacta_br":
      return "lacta_br";

    case "oreo be":
      return "oreo_be_us";

    case "oreo dcomm":
      return "oreo_dcomm_mx";

    case "clif bar":
      return "clif_bar";
    default:
      return id.includes(" - ") ? id : id.toLowerCase().replaceAll(" ", "_");
  }
};

/**
 * Get brand id used in DE "Product_Attribute" collection
 */
export const getBrandIdForProductAttribute = (brandId: string) => {
  const normalizedId = normalizeBrandId(brandId);

  switch (normalizedId) {
    case "chips":
    case "chips_us_dcomm":
      return "CHIPS AHOY!";
    case "oreo":
    case "oreo_dcomm_mx":
    case "oreo_dcomm_in":
    case "oreo_dcomm_au":
      return "OREO";

    default:
      return "";
  }
};

/**
 * Get brand name used in DE "Reference_Asset_Metadata" collection
 */
export const getBrandName = (brandName: string) => {
  if (!brandName) return "";

  const normalizedId = brandName
    ?.toLowerCase()
    ?.replaceAll(/[^a-z\s-]/g, "")
    ?.trim();

  switch (normalizedId) {
    case "chipsahoy":
    case "chips ahoy":
    case "chips":
    case "chip":
    case "ahoy":
    case "chipahoy":
    case "cookies":
      return "CHIPS-AHOY";

    case "chips ahoy dcomm":
      return "Chips Ahoy!-Dcomm"; // NOTE: this its the brand name on CDS/DE (case sensitive)

    case "oreo":
    case "oreocookies":
    case "oreo cookies":
    case "ore":
      return "OREO";

    case "milka":
    case "milkachocolate":
    case "milka chocolate":
    case "milk":
      return "MILKA-MMMAX";

    case "cadbury":
    case "cad":
      return "CADBURY";

    case "lacta":
      return "LACTA";

    case "oreo be":
      return "OREO BE";

    case "oreo dcomm":
      return "OREO dComm";

    default:
      return "";
  }
};

/**
 * Get region name used in DE "Reference_Asset_Metadata" collection
 */
export const getRegionName = (locationName: string) => {
  if (!locationName) return "";

  const normalizedId = locationName
    ?.toLowerCase()
    ?.replaceAll(/[^a-z\s-]/g, "")
    ?.trim();

  switch (normalizedId) {
    case "us":
    case "usa":
      return "NA";

    case "eu":
    case "de":
      return "MEU";

    case "uk":
    case "gb":
      return "UK&I";

    case "brazil":
    case "br":
      return "LATAM";

    case "mx":
      return "MX";

    case "in":
      return "SA";

    case "au":
      return "ANZ";

    default:
      return "";
  }
};

export const getBrandIdKey = (brand: string, location: string): string => {
  const normalizedBrand = brand.toLocaleLowerCase();
  const normalizedLocation = location.toLocaleLowerCase();
  return `${normalizedBrand}-${normalizedLocation}`;
};

export const getBrandIdUnderscoreFormat = (brand: string, location: string): string => {
  const normalizedBrand = brand.toLocaleLowerCase().split(" ").join("_");
  const normalizedLocation = location.toLocaleLowerCase();
  return `${normalizedBrand}_${normalizedLocation}`;
};

export const hasBrandWithGivenSubscriptionType = ({
  brand,
  location,
  brandId,
  brands,
  subscriptionType,
}: {
  brand: string;
  location: string;
  brandId: string;
  brands: BrandWithCdsMetadata[];
  subscriptionType: string;
}) => {
  return (
    brands?.filter(
      (b) => (b.brandId === brandId || (b.brand === brand && b.location === location)) && b.subscriptionType === subscriptionType,
    )?.length > 0
  );
};

export const checkUniversalBrand = ({
  brand,
  location,
  brandId,
  brands,
}: {
  brand: string;
  location: string;
  brandId: string;
  brands: BrandWithCdsMetadata[];
}) => {
  return hasBrandWithGivenSubscriptionType({ brand, location, brandId, brands, subscriptionType: AIDA_SUBSCRIPTION_TYPES.UNIVERSAL });
};

export const getDefaultPermissionsToAssign = ({
  brand,
  location,
  brandId,
  brands,
}: {
  brand: string;
  location: string;
  brandId: string;
  brands: BrandWithCdsMetadata[];
}) => {
  const defaultPermissions = getArrayFromCSVString(process.env.DEFAULT_PERMISSION_SET);
  const defaultUniversalBrandPermissions = getArrayFromCSVString(process.env.DEFAULT_UNIVERSAL_BRAND_PERMISSION_SET);
  const isUniversalBrand = checkUniversalBrand({ brand, location, brandId, brands });
  return isUniversalBrand ? defaultUniversalBrandPermissions : defaultPermissions;
};

export const isCadburyBrand = (brandId: string) => {
  const brand = brandId?.toLocaleLowerCase();
  return brand.includes(BRAND_NAMES.CADBURY);
};

export const isCadburyOrLactaBrand = (brandId: string) => {
  const brand = brandId?.toLocaleLowerCase();
  const isLactaBrand = brand.includes(BRAND_NAMES.LACTA);
  return isCadburyBrand(brandId) || isLactaBrand;
};

export const isImagenSupportedBrand = (brandId: string) => {
  const brand = brandId?.toLocaleLowerCase();
  return IMAGEN_SUPPORTED_BRANDS.some((supportedBrand) => brand?.includes(supportedBrand));
};
